/**
 * Zustand slice creator for sync queue UI state.
 *
 * Exposes `pending`, `failed`, `lastSyncAt` and three actions:
 * - `enqueue` (add to pending)
 * - `markFailed` (move from pending → failed)
 * - `setLastSyncAt`
 *
 * The slice creator is exported standalone — the global zustand store is
 * built later in section 13. We deliberately do NOT import `state/store`
 * here (it doesn't exist yet) to avoid an import cycle when wiring lands.
 */

import type { LegacySyncOp } from '@/lib/sheets/sync-queue';

/**
 * Slice-level op shape.
 *
 * Aliases `LegacySyncOp` (the loose envelope) so existing UI callers
 * continue to compile while we migrate to the discriminated `SyncOp`
 * union. The strict `SyncOp` is the long-term direction; until UI
 * callers are refactored, the slice mirrors the legacy interface.
 */
export type SliceSyncOp = LegacySyncOp;

export interface SyncState {
  pending: SliceSyncOp[];
  failed: SliceSyncOp[];
  lastSyncAt: string | null;
}

export interface SyncActions {
  enqueue: (op: SliceSyncOp) => void;
  markFailed: (opId: string, error: string) => void;
  removePending: (opId: string) => void;
  setLastSyncAt: (iso: string) => void;
  reset: () => void;
}

export type SyncSlice = SyncState & SyncActions;

export const initialSyncState: SyncState = {
  pending: [],
  failed: [],
  lastSyncAt: null,
};

/**
 * Zustand slice creator. Compatible with `zustand`'s `StateCreator` shape
 * (`(set, get, api) => SyncSlice`) without forcing a dependency on the
 * concrete zustand types here — the store wires this up later.
 */
export const createSyncSlice =
  <T extends SyncSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
  ): SyncSlice => ({
    ...initialSyncState,

    enqueue: (op) =>
      set((state) => ({
        pending: [...state.pending, op],
      } as Partial<T>)),

    markFailed: (opId, error) =>
      set((state) => {
        const op = state.pending.find((o) => o.id === opId);
        if (!op) return {} as Partial<T>;
        const failedOp: SliceSyncOp = { ...op, last_error: error };
        return {
          pending: state.pending.filter((o) => o.id !== opId),
          failed: [...state.failed, failedOp],
        } as Partial<T>;
      }),

    removePending: (opId) =>
      set((state) => ({
        pending: state.pending.filter((o) => o.id !== opId),
      } as Partial<T>)),

    setLastSyncAt: (iso) => set({ lastSyncAt: iso } as Partial<T>),

    reset: () => set({ ...initialSyncState } as Partial<T>),
  });
